import React, { useState, useEffect } from 'react';
import { Bug, CheckCircle2, Clock, AlertTriangle, AlertCircle, Info, Trash2, Eye, Filter, RefreshCw, MessageSquare, X, Lightbulb, Grid, Image as ImageIcon } from 'lucide-react';
import { apiFetch } from '../services/cloudflareService';
import { Hospital } from '../types';
import { motion, AnimatePresence } from 'framer-motion';

interface BugReport {
    id: string;
    hospitalId: string;
    reporterName: string;
    reporterRole: string;
    title: string;
    description: string | null;
    severity: 'low' | 'medium' | 'high';
    category: 'bug' | 'ux' | 'feature_request' | 'other';
    page: string | null;
    screenName: string | null;
    screenshotUrl: string | null;
    userAgent: string | null;
    resolution: string | null;
    status: 'open' | 'resolved';
    resolvedAt: string | null;
    createdAt: string;
}

interface BugReportsPanelProps {
    hospitals: Hospital[];
    t: Record<string, string>;
}

const SEVERITY_CONFIG = {
    low: { label: 'Menor', icon: Info, color: 'text-blue-500', bg: 'bg-blue-500/10', dot: 'bg-blue-500' },
    medium: { label: 'Molesto', icon: AlertCircle, color: 'text-amber-500', bg: 'bg-amber-500/10', dot: 'bg-amber-500' },
    high: { label: 'Crítico', icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-500/10', dot: 'bg-red-500' },
};

const CATEGORY_CONFIG = {
    bug: { label: 'Error / Bug', icon: Bug, color: 'text-rose-500', bg: 'bg-rose-50' },
    ux: { label: 'UX / Interfaz', icon: MessageSquare, color: 'text-indigo-500', bg: 'bg-indigo-50' },
    feature_request: { label: 'Mejora', icon: Lightbulb, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    other: { label: 'Otro', icon: Grid, color: 'text-slate-500', bg: 'bg-slate-50' },
};

// Calculate time ago
const timeAgo = (dateStr: string) => {
    const min = Math.round((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (min < 60) return `hace ${min} min`;
    const hs = Math.round(min / 60);
    if (hs < 24) return `hace ${hs} h`;
    return `hace ${Math.round(hs / 24)} d`;
};

const BugReportsPanel: React.FC<BugReportsPanelProps> = ({ hospitals, t }) => {
    const [bugs, setBugs] = useState<BugReport[]>([]);
    const [loading, setLoading] = useState(true);
    
    // Filters
    const [filterStatus, setFilterStatus] = useState<'all' | 'open' | 'resolved'>('open');
    const [filterHospital, setFilterHospital] = useState<string>('');
    const [filterCategory, setFilterCategory] = useState<string>('all');
    
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [resolutionText, setResolutionText] = useState('');
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [stats, setStats] = useState<any>(null);

    const loadBugs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filterStatus !== 'all') params.set('status', filterStatus);
            if (filterHospital) params.set('hospitalId', filterHospital);
            if (filterCategory !== 'all') params.set('category', filterCategory);
            
            const [resBugs, resStats] = await Promise.all([
                apiFetch(`/api/bugs?${params.toString()}`, { credentials: 'include' }),
                apiFetch(`/api/bugs/stats${filterHospital ? `?hospitalId=${filterHospital}` : ''}`, { credentials: 'include' })
            ]);
            
            if (resBugs.ok) setBugs(await resBugs.json());
            if (resStats.ok) setStats(await resStats.json());
        } catch (e) {
            console.error('Failed to load bugs:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadBugs(); }, [filterStatus, filterHospital, filterCategory]);

    const handleResolve = async (bugId: string) => {
        await apiFetch(`/api/bugs/${bugId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ status: 'resolved', resolution: resolutionText || null }),
        });
        setResolutionText('');
        setExpandedId(null);
        loadBugs();
    };

    const handleReopen = async (bugId: string) => {
        await apiFetch(`/api/bugs/${bugId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ status: 'open', resolution: null }),
        });
        loadBugs();
    };

    const handleDelete = async (bugId: string) => {
        if (!window.confirm('¿Eliminar este reporte permanentemente?')) return;
        await apiFetch(`/api/bugs/${bugId}`, { method: 'DELETE', credentials: 'include' });
        loadBugs();
    };

    const getHospitalName = (hId: string) => hospitals.find(h => h.id === hId)?.name || hId;

    const openCount = stats?.open || 0;
    const avgResolutionHs = stats?.avgResolutionMinutes ? Math.round(stats.avgResolutionMinutes / 60) : 0;

    return (
        <div className="space-y-6">
            {/* Header Stats Dashboard */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className={`flex items-center gap-4 ${t.cardBg} rounded-2xl px-5 py-4 border ${t.cardBorder} ${t.cardShadow}`}>
                    <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center shrink-0">
                        <Bug className="w-6 h-6 text-indigo-500" />
                    </div>
                    <div>
                        <p className={`text-2xl font-black tracking-tight ${t.textWhite}`}>{openCount}</p>
                        <p className={`text-[10px] font-bold uppercase tracking-widest ${t.textMuted}`}>Abiertos</p>
                    </div>
                </div>
                
                <div className={`flex items-center gap-4 ${t.cardBg} rounded-2xl px-5 py-4 border ${t.cardBorder} ${t.cardShadow}`}>
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                        <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                    </div>
                    <div>
                        <p className={`text-2xl font-black tracking-tight ${t.textWhite}`}>{stats?.resolved || 0}</p>
                        <p className={`text-[10px] font-bold uppercase tracking-widest ${t.textMuted}`}>Resueltos</p>
                    </div>
                </div>

                <div className={`flex items-center gap-4 ${t.cardBg} rounded-2xl px-5 py-4 border ${t.cardBorder} ${t.cardShadow}`}>
                    <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                        <Clock className="w-6 h-6 text-amber-500" />
                    </div>
                    <div>
                        <p className={`text-2xl font-black tracking-tight ${t.textWhite}`}>{avgResolutionHs}<span className="text-sm ml-1 text-slate-400">hs</span></p>
                        <p className={`text-[10px] font-bold uppercase tracking-widest ${t.textMuted}`}>Promedio Resolución</p>
                    </div>
                </div>

                {stats?.bySeverity?.find((s:any) => s.severity === 'high')?.count > 0 ? (
                    <div className={`flex items-center gap-4 bg-red-500 text-white rounded-2xl px-5 py-4 border border-red-600 shadow-lg shadow-red-500/20 animate-pulse`}>
                        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                            <AlertTriangle className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-2xl font-black tracking-tight">{stats.bySeverity.find((s:any) => s.severity === 'high').count}</p>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-red-100">Críticos Pendientes</p>
                        </div>
                    </div>
                ) : (
                    <div className={`flex items-center gap-4 ${t.cardBg} rounded-2xl px-5 py-4 border ${t.cardBorder} ${t.cardShadow} opacity-60`}>
                        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                            <AlertTriangle className="w-6 h-6 text-slate-400" />
                        </div>
                        <div>
                            <p className={`text-2xl font-black tracking-tight ${t.textWhite}`}>0</p>
                            <p className={`text-[10px] font-bold uppercase tracking-widest ${t.textMuted}`}>Críticos</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Filters Row */}
            <div className="flex flex-wrap items-center gap-3">
                <select
                    value={filterHospital}
                    onChange={(e) => setFilterHospital(e.target.value)}
                    className={`text-xs font-bold px-3 py-2 rounded-xl border ${t.cardBorder} ${t.cardBg} ${t.textMain} outline-none`}
                >
                    <option value="">Todos los hospitales</option>
                    {hospitals.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                </select>

                <div className="flex bg-slate-100 rounded-xl p-0.5">
                    {(['all', 'bug', 'ux', 'feature_request', 'other'] as const).map(c => (
                        <button
                            key={c}
                            onClick={() => setFilterCategory(c)}
                            className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all flex items-center gap-1.5 ${
                                filterCategory === c
                                    ? 'bg-white shadow text-indigo-600'
                                    : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            {c === 'all' ? 'Todas las Categorías' : CATEGORY_CONFIG[c as keyof typeof CATEGORY_CONFIG].label}
                        </button>
                    ))}
                </div>

                <div className="flex-1" />

                <div className="flex bg-slate-100 rounded-xl p-0.5">
                    {(['open', 'resolved', 'all'] as const).map(s => (
                        <button
                            key={s}
                            onClick={() => setFilterStatus(s)}
                            className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all ${
                                filterStatus === s
                                    ? 'bg-white shadow text-indigo-600'
                                    : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            {s === 'open' ? 'Abiertos' : s === 'resolved' ? 'Resueltos' : 'Todos'}
                        </button>
                    ))}
                </div>

                <button onClick={loadBugs} className={`p-2 rounded-xl ${t.cardBg} border ${t.cardBorder} hover:bg-indigo-50 transition-colors`}>
                    <RefreshCw className={`w-4 h-4 ${t.textMuted} ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {/* Bug List */}
            {loading ? (
                <div className="text-center py-20">
                    <div className="w-6 h-6 border-2 border-indigo-200 border-t-indigo-500 rounded-full animate-spin mx-auto" />
                </div>
            ) : bugs.length === 0 ? (
                <div className={`text-center py-20 ${t.cardBg} rounded-2xl border ${t.cardBorder}`}>
                    <CheckCircle2 className={`w-12 h-12 mx-auto mb-4 text-emerald-500 opacity-50`} />
                    <p className={`text-sm font-bold ${t.textMuted}`}>
                        {filterStatus === 'open' ? '¡Bandeja en ceros! Todo en orden 🎉' : 'No hay reportes.'}
                    </p>
                </div>
            ) : (
                <div className="space-y-2">
                    {bugs.map(bug => {
                        const sev = SEVERITY_CONFIG[bug.severity];
                        const cat = CATEGORY_CONFIG[bug.category] || CATEGORY_CONFIG.bug;
                        const isExpanded = expandedId === bug.id;
                        
                        return (
                            <motion.div
                                key={bug.id}
                                layout
                                className={`${t.cardBg} rounded-2xl border ${t.cardBorder} ${t.cardShadow} overflow-hidden transition-all ${
                                    bug.status === 'resolved' ? 'opacity-60 hover:opacity-100' : ''
                                }`}
                            >
                                {/* Main Row */}
                                <div
                                    className={`px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer ${t.tableRowHover} transition-colors`}
                                    onClick={() => setExpandedId(isExpanded ? null : bug.id)}
                                >
                                    <div className="flex items-center gap-4 flex-1 min-w-0">
                                        {/* Category Icon */}
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${cat.bg} ${cat.color} ${bug.status === 'open' && bug.severity === 'high' ? 'ring-2 ring-red-500 ring-offset-2 animate-pulse' : ''}`}>
                                            <cat.icon className="w-5 h-5" />
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <p className={`text-[15px] font-black truncate ${t.textWhite}`}>{bug.title}</p>
                                                {bug.screenshotUrl && <ImageIcon className="w-3.5 h-3.5 text-indigo-400 shrink-0" title="Contiene captura de pantalla" />}
                                            </div>
                                            <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] ${t.textMuted}`}>
                                                <span className="font-bold text-slate-600">{getHospitalName(bug.hospitalId)}</span>
                                                <span className="opacity-50">•</span>
                                                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {timeAgo(bug.createdAt)}</span>
                                                <span className="opacity-50">•</span>
                                                <span className="truncate max-w-[120px]">{bug.reporterName}</span>
                                                {bug.screenName && (
                                                    <>
                                                        <span className="opacity-50">•</span>
                                                        <span className="truncate max-w-[150px]">{bug.screenName}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Side Badges */}
                                    <div className="flex items-center gap-2 pl-[56px] sm:pl-0 shrink-0">
                                        {bug.status === 'resolved' ? (
                                            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider text-emerald-600 bg-emerald-50 border border-emerald-200">
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Resuelto
                                            </span>
                                        ) : (
                                            <span className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider ${sev.bg} ${sev.color} border border-current/20`}>
                                                <sev.icon className="w-3.5 h-3.5" />
                                                {sev.label}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Expanded Detail */}
                                <AnimatePresence>
                                    {isExpanded && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="overflow-hidden"
                                        >
                                            <div className={`px-[56px] sm:px-[76px] pb-5 pt-2`}>
                                                {bug.description && (
                                                    <div className="mb-5 bg-slate-50 p-4 rounded-xl border border-slate-100">
                                                        <p className={`text-[10px] font-black uppercase tracking-widest mb-1.5 ${t.textMuted}`}>Descripción Detallada</p>
                                                        <p className={`text-sm ${t.textMain} leading-relaxed whitespace-pre-wrap`}>{bug.description}</p>
                                                    </div>
                                                )}

                                                {/* Meta Info Grid */}
                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
                                                    <div>
                                                        <span className={`block text-[10px] font-bold uppercase tracking-widest mb-1 ${t.textMuted}`}>Categoría</span>
                                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold ${cat.bg} ${cat.color}`}>{cat.label}</span>
                                                    </div>
                                                    {bug.page && (
                                                        <div className="col-span-2 sm:col-span-1">
                                                            <span className={`block text-[10px] font-bold uppercase tracking-widest mb-1 ${t.textMuted}`}>Ruta / URL</span>
                                                            <code className={`text-[10px] font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-500 truncate block`}>{bug.page}</code>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <span className={`block text-[10px] font-bold uppercase tracking-widest mb-1 ${t.textMuted}`}>Rol</span>
                                                        <span className={`text-[11px] font-medium ${t.textMain} uppercase`}>{bug.reporterRole}</span>
                                                    </div>
                                                    {bug.resolvedAt && (
                                                        <div>
                                                            <span className={`block text-[10px] font-bold uppercase tracking-widest mb-1 ${t.textMuted}`}>Cerrado el</span>
                                                            <span className={`text-[11px] font-medium ${t.textMain}`}>
                                                                {new Date(bug.resolvedAt).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                {bug.screenshotUrl && (
                                                    <div className="mb-5">
                                                        <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${t.textMuted}`}>Captura de Pantalla Adjunta</p>
                                                        <div 
                                                            className="relative rounded-xl overflow-hidden border border-slate-200 cursor-pointer group bg-slate-100"
                                                            onClick={(e) => { e.stopPropagation(); setPreviewImage(bug.screenshotUrl); }}
                                                        >
                                                            <img src={bug.screenshotUrl} alt="Evidencia" className="w-full max-h-[300px] object-contain" />
                                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                                                <span className="bg-white text-slate-900 px-4 py-2 rounded-xl text-xs font-bold shadow-xl opacity-0 group-hover:opacity-100 transition-all flex items-center gap-2 transform translate-y-2 group-hover:translate-y-0">
                                                                    <Eye className="w-4 h-4" /> Ver tamaño completo
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {bug.resolution && (
                                                    <div className="mb-5 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                                                        <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600 mb-1">Nota de Resolución</p>
                                                        <p className="text-sm font-medium text-emerald-800">{bug.resolution}</p>
                                                    </div>
                                                )}

                                                {/* Actions */}
                                                <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
                                                    {bug.status === 'open' ? (
                                                        <>
                                                            <input
                                                                type="text"
                                                                value={resolutionText}
                                                                onChange={(e) => setResolutionText(e.target.value)}
                                                                placeholder="Escribe cómo lo resolviste (opcional)..."
                                                                className={`flex-1 min-w-[200px] px-4 py-2.5 rounded-xl text-xs font-medium border ${t.cardBorder} ${t.cardBg} ${t.textMain} outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 transition-all`}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleResolve(bug.id); }}
                                                                className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest bg-emerald-500 text-white hover:bg-emerald-600 transition-all shadow-lg shadow-emerald-500/20"
                                                            >
                                                                <CheckCircle2 className="w-4 h-4" /> Marcar Resuelto
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleReopen(bug.id); }}
                                                            className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest ${t.cardBg} border ${t.cardBorder} ${t.textMain} hover:border-amber-400 transition-colors`}
                                                        >
                                                            <RefreshCw className="w-4 h-4" /> Reabrir Ticket
                                                        </button>
                                                    )}
                                                    <div className="flex-1" />
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleDelete(bug.id); }}
                                                        className="p-2.5 rounded-xl text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                        title="Eliminar permanentemente"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        );
                    })}
                </div>
            )}

            {/* Lightbox Preview */}
            <AnimatePresence>
                {previewImage && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[99999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out"
                        onClick={() => setPreviewImage(null)}
                    >
                        <button className="absolute top-6 right-6 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur transition-colors">
                            <X className="w-6 h-6" />
                        </button>
                        <motion.img 
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.95 }}
                            src={previewImage} 
                            alt="Screenshot Full" 
                            className="max-w-full max-h-full object-contain rounded-xl shadow-2xl" 
                            onClick={(e) => e.stopPropagation()}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default BugReportsPanel;
